import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import styled, { useTheme } from 'styled-components';
import {
  ArrowRight,
  Gauge,
  Lock,
  Shield,
  Sparkles,
  Users,
} from 'lucide-react';
import PackageDialog from '../course/packagedialog';
import { gateMessage, gateTitle, isRoleRestrictedGate } from '../services/apiGate';
import { isNonOwnerOrgStaff } from '../services/instructorPortalAccess';
import { formatPlanLabel } from '../admin/Packages/planDisplayUtils';
import {
  Sheet,
  CloseWrap,
  Head,
  Title,
  Sub,
  Cta,
  activationCodeDialogPaperProps,
  getPlanTierDialogBackdropProps,
  getTierVisualTheme,
} from './planTierPresentation';

/** Gate sheet — compact card surface aligned with activation-code dialog. */
const GateSheet = styled(Sheet)`
  background: ${({ theme }) => theme.surface};
  padding: 28px clamp(22px, 3vw, 32px) 24px;
`;

const IconRing = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 14px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $border }) => $border};
  color: ${({ $color }) => $color};
  box-shadow: 0 8px 24px -12px ${({ $shadow }) => $shadow};
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  margin-bottom: 10px;
  border-radius: ${({ theme }) => theme.rPill};
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $border }) => $border};
`;

const TierCard = styled.div`
  max-width: 360px;
  margin: 18px auto 22px;
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.rLg};
  border: 1px solid ${({ theme }) => theme.accentLine};
  background: ${({ theme }) => theme.accentSoft};
  display: grid;
  gap: 10px;
`;

const TierRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: ${({ theme }) => theme.fsSm};

  span:first-child {
    color: ${({ theme }) => theme.textMuted};
    font-weight: 600;
  }

  strong {
    color: ${({ theme }) => theme.text};
    font-weight: 700;
    text-transform: capitalize;
  }
`;

const TierDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.border};
`;

const RequiredTier = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  span:first-child {
    font-size: ${({ theme }) => theme.fsSm};
    color: ${({ theme }) => theme.textMuted};
    font-weight: 600;
  }

  em {
    font-style: normal;
    font-size: ${({ theme }) => theme.fsSm};
    font-weight: 750;
    color: ${({ $color }) => $color};
    letter-spacing: -0.01em;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 360px;
  margin: 0 auto;
`;

const DismissLink = styled.button`
  margin: 4px auto 0;
  padding: 8px 12px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.textMuted};
  font-family: ${({ theme }) => theme.fontBody};
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 600;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.rMd};

  &:hover {
    color: ${({ theme }) => theme.text};
    text-decoration: underline;
  }
`;

function formatTierLabel(tier) {
  if (!tier) {
    return 'Current plan';
  }
  return formatPlanLabel(tier);
}

function resolveGateVisuals(gateBody, theme) {
  const gate = gateBody || {};
  const isLimit = gate.error === 'limit_reached';
  // A role restriction wears the same shield treatment as the billing lock — no plan to buy.
  const isBillingLock = gate.feature === 'billing_manage' || isRoleRestrictedGate(gate);

  if (isBillingLock) {
    return {
      eyebrow: gate.feature === 'billing_manage' ? 'Admin access' : 'Access restricted',
      Icon: Shield,
      ring: {
        bg: `color-mix(in oklch, ${theme.warning || 'oklch(0.74 0.15 72)'} 14%, ${theme.surface})`,
        border: `color-mix(in oklch, ${theme.warning || 'oklch(0.74 0.15 72)'} 28%, ${theme.border})`,
        color: `color-mix(in oklch, ${theme.warning || 'oklch(0.74 0.15 72)'} 82%, ${theme.text})`,
        shadow: 'rgba(20, 40, 28, 0.12)',
      },
      badge: {
        bg: `color-mix(in oklch, ${theme.warning || 'oklch(0.74 0.15 72)'} 14%, ${theme.surface})`,
        border: `color-mix(in oklch, ${theme.warning || 'oklch(0.74 0.15 72)'} 24%, transparent)`,
        color: `color-mix(in oklch, ${theme.warning || 'oklch(0.74 0.15 72)'} 78%, ${theme.text})`,
      },
    };
  }

  if (isLimit) {
    return {
      eyebrow: 'Plan limit',
      Icon: Gauge,
      ring: {
        bg: `color-mix(in oklch, ${theme.danger} 10%, ${theme.surface})`,
        border: `color-mix(in oklch, ${theme.danger} 22%, ${theme.border})`,
        color: theme.danger,
        shadow: 'rgba(192, 57, 43, 0.18)',
      },
      badge: {
        bg: `color-mix(in oklch, ${theme.danger} 10%, ${theme.surface})`,
        border: `color-mix(in oklch, ${theme.danger} 22%, transparent)`,
        color: theme.danger,
      },
    };
  }

  if (gate.feature === 'ai_course_builder') {
    return {
      eyebrow: 'Feature locked',
      Icon: Sparkles,
      ring: {
        bg: theme.accentSoft,
        border: theme.accentLine,
        color: theme.accentStrong,
        shadow: 'rgba(91, 84, 230, 0.18)',
      },
      badge: {
        bg: theme.accentSoft,
        border: theme.accentLine,
        color: theme.accentStrong,
      },
    };
  }

  if (gate.feature === 'invite_team_members') {
    return {
      eyebrow: 'Feature locked',
      Icon: Users,
      ring: {
        bg: theme.accentSoft,
        border: theme.accentLine,
        color: theme.accentStrong,
        shadow: 'rgba(91, 84, 230, 0.18)',
      },
      badge: {
        bg: theme.accentSoft,
        border: theme.accentLine,
        color: theme.accentStrong,
      },
    };
  }

  return {
    eyebrow: 'Feature locked',
    Icon: Lock,
    ring: {
      bg: theme.accentSoft,
      border: theme.accentLine,
      color: theme.accentStrong,
      shadow: 'rgba(91, 84, 230, 0.18)',
    },
    badge: {
      bg: theme.accentSoft,
      border: theme.accentLine,
      color: theme.accentStrong,
    },
  };
}

/**
 * Global upgrade prompt when the API returns HTTP 402 (meter or feature gate).
 */
export default function UpgradeGateDialog({ open, gateBody, entitlements, onClose }) {
  const theme = useTheme();
  const [showPlans, setShowPlans] = useState(false);
  // No upgrade path when the viewer's plan already clears the requirement — the block is a
  // role restriction, and the tier card would otherwise read "you need a plan you already have".
  const roleRestricted = isRoleRestrictedGate(gateBody || {});
  // Only the org owner can buy plans. Admins/instructors are positively identified by role
  // (same convention as the plan-expired notice) and get "ask your owner" instead of a
  // checkout CTA they can't act on; an unknown role keeps the upgrade path.
  const viewerIsNonOwnerStaff = isNonOwnerOrgStaff(entitlements);
  const canUpgrade = !roleRestricted && !viewerIsNonOwnerStaff;
  const message = gateMessage(gateBody || {});
  const title = gateTitle(gateBody || {});
  const visuals = useMemo(() => resolveGateVisuals(gateBody, theme), [gateBody, theme]);
  const GateIcon = visuals.Icon;
  const upgradeTier = gateBody?.upgrade_to;
  const currentTier = gateBody?.current_tier;
  const upgradeTheme = upgradeTier ? getTierVisualTheme(upgradeTier, theme) : null;
  // Non-owner staff still see the plan context (useful when relaying to the owner),
  // just without the checkout CTA.
  const showTierCard = !roleRestricted && (currentTier || upgradeTier);

  return (
    <>
      <Dialog
        open={open && !showPlans}
        onClose={onClose}
        maxWidth={false}
        PaperProps={activationCodeDialogPaperProps}
        slotProps={{ backdrop: getPlanTierDialogBackdropProps(theme) }}
        aria-labelledby="upgrade-gate-title"
      >
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <GateSheet>
            <CloseWrap>
              <IconButton
                onClick={onClose}
                aria-label="Close upgrade dialog"
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </CloseWrap>

            <IconRing
              $bg={visuals.ring.bg}
              $border={visuals.ring.border}
              $color={visuals.ring.color}
              $shadow={visuals.ring.shadow}
              aria-hidden="true"
            >
              <GateIcon size={24} strokeWidth={2} />
            </IconRing>

            <Head>
              <Eyebrow
                $bg={visuals.badge.bg}
                $border={visuals.badge.border}
                $color={visuals.badge.color}
              >
                {visuals.eyebrow}
              </Eyebrow>
              <Title id="upgrade-gate-title">{title}</Title>
              <Sub>{message}</Sub>
              {viewerIsNonOwnerStaff && !roleRestricted ? (
                <Sub>
                  Only your <b>organisation owner</b> can upgrade the plan — please reach
                  out to them to raise the limit.
                </Sub>
              ) : null}
            </Head>

            {showTierCard ? (
              <TierCard>
                {currentTier ? (
                  <TierRow>
                    <span>Current plan</span>
                    <strong>{formatTierLabel(currentTier)}</strong>
                  </TierRow>
                ) : null}
                {currentTier && upgradeTier ? <TierDivider /> : null}
                {upgradeTier ? (
                  <RequiredTier $color={upgradeTheme?.ctaBg || theme.accent}>
                    <span>Required</span>
                    <em>{formatTierLabel(upgradeTier)} or higher</em>
                  </RequiredTier>
                ) : null}
              </TierCard>
            ) : null}

            <Actions>
              {canUpgrade ? (
                <Cta
                  type="button"
                  $bg={theme.accent}
                  $fg={theme.onAccent}
                  onClick={() => setShowPlans(true)}
                >
                  <span>View plans</span>
                  <ArrowRight size={16} strokeWidth={2.25} aria-hidden="true" />
                </Cta>
              ) : null}
              <DismissLink type="button" onClick={onClose}>
                Not now
              </DismissLink>
            </Actions>
          </GateSheet>
        </DialogContent>
      </Dialog>
      {canUpgrade ? (
        <PackageDialog
          open={showPlans}
          handleClose={() => {
            setShowPlans(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}
